import axios from 'axios';
import { useMutation, useQuery, useQueryClient, UseQueryResult } from 'react-query';
import useLog from 'src/hooks/useLog';
import { useFileUploader } from 'src/hooks/useFileUploader';
import type {
  BulkDeviceProvision,
  BulkDeviceProvisionList,
  BulkProvisionResultDownload,
  BulkProvisionUploadTarget,
  BulkProvisionValidation,
  CreateBulkDeviceProvisionRequest,
  CreatedBulkDeviceProvision,
} from 'src/types/BulkDeviceProvision';
import { isBulkProvisionActive } from 'src/types/BulkDeviceProvision';
import { DeviceType } from 'src/types/DeviceType';
import { get, post } from '../utils';

enum QueryKeys {
  BulkDeviceProvisions = 'BulkDeviceProvisions',
  BulkDeviceProvision = 'BulkDeviceProvision',
}

const ROUTE = 'bulk-device-provision';

/** How often an in-flight batch's progress is re-read. Batches run for minutes to over an hour. */
/**
 * Skew-corrected server clock.
 *
 * Staleness of a RUNNING batch is a comparison between its `updatedAt` (written by the API) and "now".
 * Using the workstation clock for "now" makes that comparison wrong by however far the clock is off,
 * and a machine running behind would keep a genuinely stranded batch permanently non-retryable — with
 * retry being its only recovery path. The batch's own `updatedAt` cannot substitute: for a stranded
 * batch it is frozen at its last heartbeat, which is exactly the value being measured.
 *
 * Every HTTP response carries the server's clock in its `Date` header, so one read-only interceptor
 * gives us the offset without changing any request signature or adding a round trip.
 */
let serverClockSkewMs = 0;
let interceptorRegistered = false;

const registerServerClockInterceptor = () => {
  if (interceptorRegistered) return;
  // A test that mocks axios (`jest.mock('axios', () => ({ default: { get, post, … } }))`) has no
  // interceptor registry, and this runs at IMPORT time — reaching into it unguarded threw while the module
  // graph was loading and took two unrelated device suites down with it.
  if (!axios?.interceptors?.response) return;
  interceptorRegistered = true;

  axios.interceptors.response.use((response) => {
    const header = response?.headers?.date;
    const serverMs = header ? new Date(header).getTime() : NaN;

    if (Number.isFinite(serverMs)) {
      serverClockSkewMs = serverMs - Date.now();
    }

    return response;
  });
};

registerServerClockInterceptor();

/** Server "now", as best we know it. Falls back to the local clock until a response has been seen. */
export const serverNow = (): number => Date.now() + serverClockSkewMs;

export const BULK_PROVISION_POLL_INTERVAL_MS = 2000;

/**
 * `fileSize` is pinned into the presigned PUT's signature server-side, so S3 enforces the size cap at
 * upload time instead of it only being discovered later during validation.
 */
export function getBulkProvisionUploadTarget(fileSize: number): Promise<BulkProvisionUploadTarget> {
  return post<BulkProvisionUploadTarget>(`${ROUTE}/upload-url`, { fileSize });
}

export function validateBulkProvisionUpload(uploadId: string, deviceType: DeviceType): Promise<BulkProvisionValidation> {
  return post<BulkProvisionValidation>(`${ROUTE}/validate`, { uploadId, deviceType });
}

/**
 * Two-step upload (the same shape as the CSV sample import and OAuth logo flows): ask the API for a
 * presigned PUT, send the bytes straight to S3 from the browser, then have the API validate the
 * object it now owns. The API never receives the file body.
 */
export function useUploadBulkProvisionCsvMutation() {
  const log = useLog();
  const { uploadFile, isUploadingFile, fileUploadProgress } = useFileUploader();

  const mutation = useMutation<BulkProvisionValidation, Error, { file: File; deviceType: DeviceType }>(
    async ({ file, deviceType }) => {
      // The presigned PUT is signed for text/csv, and useFileUploader sends the File's own type as
      // Content-Type. A .csv pick often reports application/vnd.ms-excel (or nothing at all) when
      // Excel is installed, which S3 then rejects as a signature mismatch — so re-wrap it as text/csv.
      // Re-wrapped first so the size we declare is the size we actually send.
      const csvFile = file.type === 'text/csv' ? file : new File([file], file.name, { type: 'text/csv' });

      const { uploadId, preSignedUrl } = await getBulkProvisionUploadTarget(csvFile.size);

      const uploaded = await uploadFile(preSignedUrl, csvFile);
      if (!uploaded) {
        throw new Error(`Failed to upload ${file.name}`);
      }

      return validateBulkProvisionUpload(uploadId, deviceType);
    },
    {
      onError: (err) => log.error(`There was a problem uploading the CSV file: ${err.message}`, err),
    }
  );

  return { ...mutation, isUploadingFile, fileUploadProgress };
}

export function useCreateBulkProvisionMutation() {
  const queryClient = useQueryClient();
  const log = useLog();

  return useMutation<CreatedBulkDeviceProvision, Error, CreateBulkDeviceProvisionRequest>(
    (data) => post<CreatedBulkDeviceProvision>(ROUTE, data),
    {
      onSuccess: (created) => {
        queryClient.setQueryData([QueryKeys.BulkDeviceProvision, created.id], created);
      },
      onSettled: () => {
        queryClient.invalidateQueries([QueryKeys.BulkDeviceProvisions]);
      },
      onError: (err) => log.error(`There was a problem starting the bulk provision: ${err.message}`, err),
    }
  );
}

export function useQueryBulkProvisions(
  { limit, offset }: { limit: number; offset: number },
  options?: { refetchInterval?: number | false }
): UseQueryResult<BulkDeviceProvisionList> {
  const log = useLog();

  return useQuery(
    [QueryKeys.BulkDeviceProvisions, limit, offset],
    () => get<BulkDeviceProvisionList>(ROUTE, { limit: String(limit), offset: String(offset) }),
    {
      keepPreviousData: true,
      refetchInterval: options?.refetchInterval ?? false,
      onError: (err) => {
        if (err instanceof Error) {
          log.error(`There was a problem loading bulk provisions: ${err.message}`, err);
        }
      },
    }
  );
}

/**
 * Poll one batch while it is queued or running, and stop as soon as it reaches a terminal state —
 * a finished batch never changes again, so continuing to poll would just be noise.
 */
export function useQueryBulkProvision(id: string | undefined): UseQueryResult<BulkDeviceProvision> {
  const log = useLog();

  return useQuery([QueryKeys.BulkDeviceProvision, id], () => get<BulkDeviceProvision>(`${ROUTE}/${id}`), {
    enabled: Boolean(id),
    refetchInterval: (data) => (data && isBulkProvisionActive(data) ? BULK_PROVISION_POLL_INTERVAL_MS : false),
    onError: (err) => {
      if (err instanceof Error) {
        log.error(`There was a problem loading the bulk provision: ${err.message}`, err);
      }
    },
  });
}

const useBatchActionMutation = (action: 'cancel' | 'retry', failureMessage: string) => {
  const queryClient = useQueryClient();
  const log = useLog();

  return useMutation<BulkDeviceProvision, Error, string>(
    (id) => post<BulkDeviceProvision>(`${ROUTE}/${id}/${action}`, {}),
    {
      onSuccess: (batch) => {
        queryClient.setQueryData([QueryKeys.BulkDeviceProvision, batch.id], batch);
      },
      onSettled: (_batch, _err, id) => {
        queryClient.invalidateQueries([QueryKeys.BulkDeviceProvisions]);
        // ALSO on failure, and keyed on the id we acted on: a 400 "already COMPLETED / cannot be
        // cancelled" is itself proof that the cached detail is stale — it describes a batch that has
        // moved on. Leaving the cache alone kept the progress card asserting the state the operator had
        // just been told was wrong, with its buttons still offering the action that had just failed.
        queryClient.invalidateQueries([QueryKeys.BulkDeviceProvision, id]);
      },
      onError: (err) => log.error(`${failureMessage}: ${err.message}`, err),
    }
  );
};

export function useCancelBulkProvisionMutation() {
  return useBatchActionMutation('cancel', 'There was a problem cancelling the bulk provision');
}

export function useRetryBulkProvisionMutation() {
  return useBatchActionMutation('retry', 'There was a problem retrying the bulk provision');
}

export function getBulkProvisionResultDownload(id: string): Promise<BulkProvisionResultDownload> {
  return get<BulkProvisionResultDownload>(`${ROUTE}/${id}/result-url`);
}

/**
 * Presigned URLs are short-lived, so one is minted per download click rather than being cached with
 * the batch. Partial results are downloadable mid-batch: unreached rows read as PENDING.
 */
export function useBulkProvisionResultDownloadMutation() {
  const log = useLog();

  return useMutation<BulkProvisionResultDownload, Error, string>((id) => getBulkProvisionResultDownload(id), {
    onError: (err) => log.error(`There was a problem preparing the results CSV: ${err.message}`, err),
  });
}
